import {
  ProductGroupViewModel,
  GetPriceAdjustmentMetadata,
  PriceAdjustmentViewModel,
  ProductViewModel,
  EmployeeDiscountViewModel
} from '@models/Product';
import { OptionsViewModel } from '@models/common.models';
import { Dispatch } from 'react-redux';
import { productService } from '@services/ProductService';
import { CoverageState, initialCoverageState } from '../coverage.reducer';

import {
  SelectCoverageProductGroupAction,
  SetProductGroupOptionsAction,
  ResetProductSelectionAction,
  SetProductOptionsAction,
  SelectProductAction,
  SetAvailableCoverageOptionsAction,
  ClearCoverageSelectionsAction,
  SelectAllCoverageOptionsAction,
  SetPriceAdjustmentOptionsAction,
  SetPriceAdjustmentSelectionAction,
  SetSellersCoverageAction,
  SetPriceAdjustmentAmountAction,
  SaveCoverageAction,
  SetEmployeeDiscountOptionsAction,
  SelectEmployeeDiscountAction,
  SetEmployeeDiscountEnabledAction,
  SetCoverageTotalPriceAction,
  SetCoverageStateAction,
  SetDefaultCoverageOptionAction,
  CoverageActionType,
  SetRenewalChangeFeeAction,
  SetPriceAdjustmentNotesAction
} from './coverage.actions.types';
import { doAsync, requestAction } from '@common/components/common.actions';
import { CoverageOption } from '@models/CoverageOption';
import { AppState } from '../../../..';
import { ContractActionType } from '../../../contract.actions';
import { isProductChannelEligibleForSellersCoverage, isSellersCoverageRequiredState } from '@common/validators';
import * as Redux from 'redux';
import { Action } from 'redux';
import { contractService } from '@services/ContractService';
import { CoverageForm } from '../_components/product-n-coverages.component';
import { dateParser } from '@common/dateParser';
import { actions } from 'react-redux-form';
import FormNames from '@constants/FormNames.enum';

export function resetProductSelection(): ResetProductSelectionAction {
  return {
    type: CoverageActionType.ResetProductSelection
  };
}

/* Product Groups *******************************************************************************************/
export function selectCoverageProductGroup(group: ProductGroupViewModel): SelectCoverageProductGroupAction {
  return {
    type: CoverageActionType.SelectCoverageProductGroup,
    productGroup: group
  };
}

export function getProductGroups(zipCode: string, propertySaleType: number, propertyType: number) {
  return async (dispatch: Dispatch<CoverageState>) => {
    return dispatch(
      requestAction(
        () => productService.getProductGroups(zipCode, propertySaleType, propertyType),
        setProductGroupOptions
      )
    );
  };
}

export function setProductGroupOptions(productGroups: Array<OptionsViewModel>): SetProductGroupOptionsAction {
  return {
    type: CoverageActionType.SetProductGroupOptions,
    productGroups: productGroups
  };
}

/* Products *********************************************************************************************/

export function selectProduct(product: OptionsViewModel, group: number): SelectProductAction {
  const daProduct: ProductViewModel = {
    ...product,
    productDesc: product.value,
    productChannelId: 0,
    productGroupId: group
  };
  return {
    type: CoverageActionType.SelectProduct,
    product: daProduct
  };
}

export function setProductOptions(products: Array<OptionsViewModel>): SetProductOptionsAction {
  return {
    type: CoverageActionType.SetProductOptions,
    products: products
  };
}

export function getProducts(groupId: number, propertyTypeId: number, saleTypeId: number, renewal: boolean) {
  return async (dispatch: Dispatch<CoverageState>) => {
    dispatch(
      requestAction(() => productService.getProducts(groupId, propertyTypeId, saleTypeId, renewal), setProductOptions)
    );
  };
}

export function triggerGetProducts(propertyTypeId: number, saleTypeId: number, renewal: boolean) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state = getState().coverageStep;

    if (state.selectedGroupId === undefined) {
      return;
    }

    return dispatch(getProducts(state.selectedGroupId, propertyTypeId, saleTypeId, renewal));
  };
}

/* Coverage Options ***************************************************************/

export function setDefaultCoverageOption(defaultOption: CoverageOption): SetDefaultCoverageOptionAction {
  return {
    type: CoverageActionType.SetDefaultCoverageOption,
    defaultOption: defaultOption
  };
}

export function loadDefaultCoverageOption() {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state = getState().coverageStep;
    if (!state.coverageOptions || state.coverageOptions.length === 0) {
      return;
    }
    try {
      const basicOption = state.coverageOptions.filter(option => option.isBasicCoverage)[0];
      dispatch(doAsync(setDefaultCoverageOption(basicOption))).then(() => {
        dispatch(updateTotalPrice());
      });
    } catch {
      const basicOption = state.coverageOptions[0];
      dispatch(doAsync(setDefaultCoverageOption(basicOption))).then(() => {
        dispatch(updateTotalPrice());
      });
    }
  };
}

export function selectAllCoverageOptions(optionsToSelect: Array<CoverageOption>): SelectAllCoverageOptionsAction {
  return {
    type: CoverageActionType.SelectAllCoverageOptions,
    optionsToSelect: optionsToSelect
  };
}

export function clearCoverageOptions(): ClearCoverageSelectionsAction {
  return {
    type: CoverageActionType.ClearCoverageSelections
  };
}

export function triggerGetCoverageOptions(isRenewal?: boolean, propertyTypeId?: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const app = getState();
    const state = app.coverageStep;
    if (state.selectedProduct === undefined || state.selectedGroupId === undefined) {
      return;
    }

    if (isRenewal && (app.contract || {}).contractVM && propertyTypeId !== undefined) {
      dispatch(getRenewalChangeFee(app.contract.contractVM!.id, propertyTypeId, state.selectedProduct.id));
    } else {
      dispatch(setRenewalChangeFee(0));
    }
    return dispatch(
      getCoverageOptions(
        state.selectedGroupId,
        state.selectedProduct.id,
        state.sellerCoverage,
        state.selectedCoverageOptions
      )
    ).then(() => {
      return dispatch(loadDefaultCoverageOption());
    });
  };
}

export function getCoverageOptions(
  groupId: number,
  productId: number,
  isSellerCoverage: boolean = false,
  selectedCoverageOptions: Array<CoverageOption>
) {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    return dispatch(
      requestAction(
        () => productService.getCoverageOptions(groupId, productId, isSellerCoverage, selectedCoverageOptions),
        setAvailableCoverageOptions,
        {
          overrideFailMessage: 'Unknown Error, Unable to get Coverage options.'
        }
      )
    );
  };
}

export function setAvailableCoverageOptions(coverageOptions: Array<CoverageOption>): SetAvailableCoverageOptionsAction {
  return {
    type: CoverageActionType.SetAvailableCoverageOptions,
    coverageOptions: coverageOptions
  };
}

export function toggleCoverageItem(option: CoverageOption) {
  return {
    type: CoverageActionType.ToggleCoverageOption,
    option: option
  };
}

export function updateCoverageItem(option: CoverageOption) {
  return {
    type: CoverageActionType.UpdateCoverageOption,
    option: option
  };
}

/* Renewal Change Fee ******************************************/

export function setRenewalChangeFee(renewalChangeFee: number): SetRenewalChangeFeeAction {
  return {
    type: CoverageActionType.SetRenewalChangeFee,
    renewalChangeFee: renewalChangeFee
  };
}

export function getRenewalChangeFee(contractId: number, propertyTypeId: number, productId: number) {
  return async (dispatch: Dispatch<CoverageState>) => {
    dispatch(
      requestAction(
        () => contractService.getRenewalChangeFee(contractId, propertyTypeId, productId),
        setRenewalChangeFee
      )
    );
  };
}

/* Price Adjustments ******************************************/

export function getPriceAdjustments(
  productId: number /* Dynamic wthin the component */,
  discountMetadata: GetPriceAdjustmentMetadata /* static once you are on the coverage step */
) {
  return async (dispatch: Dispatch<CoverageState>) => {
    return dispatch(
      requestAction(() => productService.getPriceAdjustments(productId, discountMetadata), setPriceAdjustmentOptions)
    );
  };
}

export function triggerGetPriceAdjustments(metadata: GetPriceAdjustmentMetadata) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state = getState().coverageStep;
    if (state.selectedProduct === undefined) {
      return;
    }

    if (state.selectedProduct.id === -1) {
      // deselect the adjustment
      return dispatch(doAsync(setPriceAdjustmentSelection(undefined)));
    }

    metadata = {
      ...metadata,
      hasEmployeeDiscounts: state.employeeDiscounts && state.employeeDiscounts.length > 0
    };

    return dispatch(getPriceAdjustments(state.selectedProduct.id, metadata));
  };
}

export function setPriceAdjustmentSelection(selection?: PriceAdjustmentViewModel): SetPriceAdjustmentSelectionAction {
  return {
    type: CoverageActionType.SetPriceAdjustmentSelection,
    selection: selection
  };
}

export function setPriceAdjustmentOptions(
  priceAdjustments: Array<PriceAdjustmentViewModel>
): SetPriceAdjustmentOptionsAction {
  return {
    type: CoverageActionType.SetPriceAdjustmentOptions,
    priceAdjustments: priceAdjustments
  };
}

export function setPriceAdjustmentAmount(amount: number): SetPriceAdjustmentAmountAction {
  return {
    type: CoverageActionType.SetPriceAdjustmentAmount,
    amount: amount
  };
}

export function setTotalPrice(
  totalBuyers: number,
  totalSellers: number,
  totalPrice: number,
  totalDiscount: number
): SetCoverageTotalPriceAction {
  return {
    type: CoverageActionType.SetCoverageTotalPrice,
    totalPrice: totalPrice,
    buyersTotalPrice: totalBuyers,
    sellersTotalPrice: totalSellers,
    totalAdjustment: totalDiscount
  };
}

export function updateTotalPrice() {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state = getState().coverageStep;
    const contractState = getState().contract;
    let days = 180;
    if (contractState.contractVM && contractState.contractVM.estimatedCoeDate) {
      const coe = dateParser.parse(contractState.contractVM.estimatedCoeDate).startOf('day');
      const today = dateParser.parse(dateParser.now().toDate()).startOf('day');
      if (coe > today) {
        days = coe.diff(today, 'days');
      } else {
        days = 0;
      }
    }

    const tempOptions = state.selectedCoverageOptions.slice();
    const sellers = state.sellerCoverage
      ? tempOptions.map(x => (x.perDiemPrice ? +x.sellersPrice * days : +x.sellersPrice)).reduce((a, b) => a + b, 0)
      : 0;
    const buyers = tempOptions.map(x => +x.price).reduce((a, b) => a + b, 0);
    const subTotal = buyers + sellers;
    let adjustedTotal = subTotal;
    let totalDiscount = 0;
    if (state.selectedPriceAdjustment && state.selectedPriceAdjustment.id >= 0) {
      const override = state.overridePriceAdjustmentAmount;
      let temp = {
        ...state.selectedPriceAdjustment
      };
      if (override !== undefined) {
        temp.adjustmentAmount = override;
      }
      if (temp.increase) {
        adjustedTotal += temp.adjustmentAmount;
        totalDiscount += temp.adjustmentAmount;
      } else {
        adjustedTotal -= temp.adjustmentAmount;
        totalDiscount -= temp.adjustmentAmount;
      }
    }

    if (state.selectedEmployeeDiscount && state.selectedEmployeeDiscount.id >= 0) {
      adjustedTotal -= adjustedTotal * state.selectedEmployeeDiscount.percentageAmount;
      totalDiscount = -(
        (subTotal - state.selectedPriceAdjustment!.adjustmentAmount) * state.selectedEmployeeDiscount.percentageAmount +
        state.selectedPriceAdjustment!.adjustmentAmount
      );
    }
    if (state.extraCharge !== 0) {
      adjustedTotal += state.extraCharge;
    }
    return dispatch(setTotalPrice(buyers, sellers, adjustedTotal, totalDiscount));
  };
}

/* Sellers Coverage */

export function setSellersCoverage(yes: boolean): SetSellersCoverageAction {
  return {
    type: CoverageActionType.SetSellersCoverage,
    value: yes
  };
}

export function updateSellersCoverage(stateAbbreviation: string) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState): Promise<void> => {
    const coverage = getState().coverageStep;
    if (coverage.selectedChannelId === undefined) {
      // do nothing
      return Promise.resolve();
    }
    if (isSellersCoverageRequiredState(stateAbbreviation)) {
      if (!coverage.sellerCoverage) {
        return dispatch(doAsync(setSellersCoverage(true)));
      }
      return Promise.resolve();
    }
    if (!isProductChannelEligibleForSellersCoverage(coverage.selectedChannelId)) {
      if (coverage.sellerCoverage) {
        return dispatch(doAsync(setSellersCoverage(false)));
      }
      return Promise.resolve();
    }
    return Promise.resolve();
  };
}

export function toggleSellersCoverage() {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state = getState().coverageStep;
    return dispatch(setSellersCoverage(!state.sellerCoverage));
  };
}

/* Other */

export function triggerSaveCoverage() {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return await dispatch(saveToContractFlow(getState().coverageStep));
  };
}

export function saveToContractFlow(state: CoverageState): SaveCoverageAction {
  return {
    type: ContractActionType.SaveCoverageStep,
    value: state
  };
}

export function saveNotes(model: CoverageForm) {
  return async (dispatch: Dispatch<AppState>) => {
    return await dispatch(setPriceAdjustmentNotes(model.priceAdjustmentNotes));
  };
}

export function setPriceAdjustmentNotes(priceAdjustmentNotes: string): SetPriceAdjustmentNotesAction {
  return {
    type: CoverageActionType.SetPriceAdjustmentNotes,
    notes: priceAdjustmentNotes
  };
}

/**
 * A function that ensures that if an incomplete CoverageState has been loaded into the store, then its missing data is filled out so other pages are not missing display values.
 * For example, if I load from a quote, productGroup only has an 'id' - this function will map it to values if the groups exist in the store and update it
 */
export function updateSelections() {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    const state = getStore().coverageStep;
    const selectedAdjustments = state.priceAdjustments.map(({ id }) => id);
    if (
      !state.isQuote &&
      state.selectedPriceAdjustment &&
      !selectedAdjustments.includes(state.selectedPriceAdjustment.id)
    ) {
      dispatch(doAsync(setPriceAdjustmentSelection(undefined)));
      dispatch(doAsync(setPriceAdjustmentAmount(0)));
    }
    const coverageStep = state;
    let promises = [];
    if (
      coverageStep.selectedProduct !== undefined &&
      coverageStep.selectedProduct.id !== -1 &&
      coverageStep.selectedGroupId !== undefined
    ) {
      promises.push(
        updateProductSelection(dispatch, coverageStep, coverageStep.selectedProduct, coverageStep.selectedGroupId)
      );
    }

    if (coverageStep.selectedProductGroup !== undefined && coverageStep.selectedProductGroup.id !== -1) {
      promises.push(updateProductGroupSelection(dispatch, coverageStep, coverageStep.selectedProductGroup));
    }

    if (
      coverageStep.selectedPriceAdjustment !== undefined &&
      coverageStep.selectedPriceAdjustment.id !== -1 &&
      !coverageStep.overridePriceAdjustmentAmount
    ) {
      promises.push(updatePriceAdjustmentSelection(dispatch, coverageStep, coverageStep.selectedPriceAdjustment));
    }

    if (coverageStep.selectedEmployeeDiscount !== undefined && coverageStep.selectedEmployeeDiscount.id !== -1) {
      promises.push(updateEmployeeDiscountSelection(dispatch, coverageStep, coverageStep.selectedEmployeeDiscount));
    }

    if (coverageStep.selectedCoverageOptions !== undefined && coverageStep.selectedCoverageOptions.length > 0) {
      // something is selected
      const availableOptions = coverageStep.coverageOptions.slice();
      let newSelections: Array<CoverageOption> = [];
      const selections = coverageStep.selectedCoverageOptions.slice();
      selections.forEach(coverage => {
        if (!coverage || !coverage.id) {
          return;
        }
        let newSelect = availableOptions.find(x => x.id === coverage.id);
        if (newSelect) {
          newSelections.push(newSelect);
        }
      });
      if (newSelections.length > 0) {
        promises.push(dispatch(doAsync(selectAllCoverageOptions(newSelections))));
      }
    }
    return Promise.all(promises);
  };
}

function findItemAndDispatchAction<T extends { id: number }, A extends Action>(
  dispatch: Dispatch<AppState>,
  itemToMatch: T,
  arrayToCheck: Array<T> | undefined,
  actionToDispatch: Redux.ActionCreator<A>
): Promise<void> {
  if (!arrayToCheck || arrayToCheck.length === 0) {
    return Promise.resolve();
  }
  const match: T | undefined = arrayToCheck.find(x => x.id === itemToMatch.id);
  if (match) {
    return dispatch(doAsync(actionToDispatch(match)));
  }
  return Promise.resolve();
}

/* helper DO NOT export */
function updateEmployeeDiscountSelection(
  dispatch: Dispatch<AppState>,
  state: CoverageState,
  employeeDisount: EmployeeDiscountViewModel
) {
  return findItemAndDispatchAction(dispatch, employeeDisount, state.employeeDiscounts, selectEmployeeDiscount);
}
/* helper DO NOT export */
function updateProductGroupSelection(dispatch: Dispatch<AppState>, state: CoverageState, group: ProductGroupViewModel) {
  return findItemAndDispatchAction(dispatch, group, state.productGroups, selectCoverageProductGroup);
}

/* helper DO NOT export */
function updatePriceAdjustmentSelection(
  dispatch: Dispatch<AppState>,
  state: CoverageState,
  adjustmemt: PriceAdjustmentViewModel
) {
  return findItemAndDispatchAction(dispatch, adjustmemt, state.priceAdjustments, setPriceAdjustmentSelection);
}

/* helper DO NOT export */
function updateProductSelection(
  dispatch: Dispatch<AppState>,
  state: CoverageState,
  product: ProductViewModel,
  groupId: number
) {
  return findItemAndDispatchAction(dispatch, product, state.products, (prod: ProductViewModel) =>
    selectProduct(prod, groupId)
  );
}

/* Employee Discounts */

export function getEmployeeDiscount(customerId?: number) {
  return async (dispatch: Dispatch<CoverageState>) => {
    if (customerId === undefined) {
      return;
    }
    return dispatch(requestAction(() => productService.getEmployeeDiscount(customerId), setEmployeeDiscountOptions));
  };
}

export function setEmployeeDiscountOptions(
  employeeDiscounts: Array<PriceAdjustmentViewModel>
): SetEmployeeDiscountOptionsAction {
  return {
    type: CoverageActionType.SetEmployeeDiscountOptions,
    employeeDiscounts: employeeDiscounts
  };
}

export function selectEmployeeDiscount(employeeDiscount?: EmployeeDiscountViewModel): SelectEmployeeDiscountAction {
  if (employeeDiscount && employeeDiscount.id === -1) {
    return {
      type: CoverageActionType.SelectEmployeeDiscount,
      selectedEmployeeDiscount: undefined
    };
  }
  return {
    type: CoverageActionType.SelectEmployeeDiscount,
    selectedEmployeeDiscount: employeeDiscount
  };
}

export function setEmployeeDiscountEnabled(enabled: boolean): SetEmployeeDiscountEnabledAction {
  return {
    type: CoverageActionType.ViewEmployeeDiscount,
    enabled: enabled
  };
}

export function loadCoverageState(state: CoverageState): SetCoverageStateAction {
  return {
    type: CoverageActionType.LoadCoverageState,
    state: state,
    force: false
  };
}

export function clearCoverageStepSelections() {
  return async (dispatch: Dispatch<AppState>) => {
    return Promise.all([
      dispatch(clearCoverageOptions()),
      dispatch(selectCoverageProductGroup({ id: -1, value: 'None' })),
      dispatch(clearProductChannelSelection()),
      dispatch(clearProductSelection())
    ]);
  };
}

/**
 * Clear the product channel selection, this also clears direct dependencies which includes:
 * Products, Price Adjustments, and Employee Discounts
 * and indirect dependencies, (coverage Options)
 */
export function clearProductChannelSelection() {
  return async (dispatch: Dispatch<AppState>) => {
    return Promise.all([
      dispatch(clearProductSelection()),
      dispatch(setPriceAdjustmentSelection()),
      dispatch(setPriceAdjustmentAmount(0)),
      dispatch(actions.reset(FormNames.PriceAdjustmentForm)),
      dispatch(selectEmployeeDiscount())
    ]);
  };
}

export function clearProductSelection() {
  // if product is cleared, then all of its dependencies must also be cleared, that's coverageOptions
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return Promise.all([dispatch(dispatch(resetProductSelection())), dispatch(clearCoverageOptions())]);
  };
}

export function resetPriceAdjustments() {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return Promise.all([
      dispatch(setPriceAdjustmentAmount(0)),
      dispatch(setPriceAdjustmentSelection()),
      dispatch(setPriceAdjustmentNotes(''))
    ]);
  };
}

export function clearCoverageStore(zipCode?: string, saleTypeId?: number, propertyTypeId?: number) {
  return async (dispatch: Dispatch<CoverageState>, getState: () => AppState) => {
    const state = await getState();
    if (zipCode && saleTypeId && propertyTypeId) {
      await dispatch({
        ...loadCoverageState({
          ...initialCoverageState,
          priceAdjustmentNotes: state.coverageStep.priceAdjustmentNotes,
          priceAdjustments: state.coverageStep.priceAdjustments,
          selectedPriceAdjustment: state.coverageStep.selectedPriceAdjustment,
          overridePriceAdjustmentAmount: state.coverageStep.overridePriceAdjustmentAmount,
          employeeDiscounts: state.coverageStep.employeeDiscounts,
          isEmployeeDiscountEnabled: state.coverageStep.isEmployeeDiscountEnabled
        } as CoverageState),
        force: true
      });
      await dispatch(actions.change('coverageForm.product.id', -1));
      return await dispatch(getProductGroups(zipCode, saleTypeId, propertyTypeId));
    } else {
      return dispatch({
        ...loadCoverageState(initialCoverageState),
        force: true
      });
    }
  };
}



// WEBPACK FOOTER //
// ./src/contract/_components/coverage/_actions/coverage.actions.tsx